import { Component, inject, OnInit, OnDestroy, signal, computed, input } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FirmaService } from '../../../../shared/services/firma.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { PraticaDaFirmare, PraticaDetailResponse, SignatureField } from '../../../../shared/models/pratica.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModaleRifiutaComponent } from '../../../../shared/components/modali/modale-rifiuta/modale-rifiuta.component';
import { ModaleSuccessoComponent } from '../../../../shared/components/modali/modale-successo/modale-successo.component';
import { ModaleErroreGenericoComponent } from '../../../../shared/components/modali/modale-errore-generico/modale-errore-generico.component';
import { ModaleConfermaAzioneComponent } from '../../../../shared/components/modali/modale-conferma-azione/modale-conferma-azione.component';
import { ModaleOtpComponent } from '../../../../shared/components/modali/modale-otp/modale-otp.component';
import { forkJoin } from 'rxjs';
import { LayoutService } from '../../../../shared/services/layout.service';

interface FlattenedPage {
	documentID: number;
	localPage: number;
	globalPage: number;
}

@Component({
	selector: 'app-firma-dettaglio',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './firma-dettaglio.component.html',
})
export class FirmaDettaglioComponent implements OnInit, OnDestroy {
	idTransaction = input<string>();
	isPublic = input<boolean>(false);

	router = inject(Router);
	firmaService = inject(FirmaService);
	sanitizer = inject(DomSanitizer);
	authService = inject(AuthService);
	modalService = inject(NgbModal);
	layoutService = inject(LayoutService);

	pratica = signal<PraticaDaFirmare>({} as any);
	praticaDetail = signal<PraticaDetailResponse | null>(null);
	pageUrls = signal<SafeUrl[]>([]);
	loadingPage = signal(false);
	loadingAction = signal(false);
	operationID = signal<string | null>(null);
	isRejected = signal(false);
	idsQueue = signal<string[]>([]);
	totalInQueue = signal<number>(0);
	currentQueueIndex = signal<number>(0);

	/** true quando l'utente arriva dal link email senza essere loggato */
	signatureCompleted = signal(false);

	zoomLevel = signal(0.5);
	currentPage = signal(1);
	totalPages = signal(0);
	flattenedPages = signal<FlattenedPage[]>([]);
	gpsInfo = signal('');
	modalOpen = false;
	isFullscreen = signal(false);
	firmaMultipla = signal(true);

	showFirmaMultipla = computed(() => {
		const pd = this.praticaDetail();
		return pd?.tipoFirma === 'R' && !!this.authService.currentUser?.cliente?.multiSignOnview;
	});

	multiValue = computed(() => this.showFirmaMultipla() && this.firmaMultipla());

	allRequiredSigned = computed(() => {
		const pd = this.praticaDetail();
		if (!pd) return false;
		if (!pd.signatureFields) return true;
		const requiredFields = pd.signatureFields.filter((f) => f.required);
		return requiredFields.every((f) => f.firmato);
	});

	isAllSigned = computed(() => {
		const pd = this.praticaDetail();
		if (!pd || !pd.signatureFields) return true;
		return pd.signatureFields.every((f) => f.firmato);
	});

	firstUnsignedField = computed(() => {
		const pd = this.praticaDetail();
		if (!pd?.signatureFields || !pd.documents?.length) return null;

		const unsigned = pd.signatureFields.filter((f) => !f.firmato);
		if (unsigned.length === 0) return null;

		const docs = pd.documents;
		// Ordina per indice del documento, quindi per numero di pagina
		return unsigned.sort((a, b) => {
			const idxA = docs.findIndex((d) => d.documentID == a.documentID);
			const idxB = docs.findIndex((d) => d.documentID == b.documentID);
			if (idxA !== idxB) return idxA - idxB;
			return (a.page || 0) - (b.page || 0);
		})[0];
	});

	firstUnsignedFieldPage = computed(() => {
		const field = this.firstUnsignedField();
		return field ? field.page : null;
	});

	goToNextUnsigned() {
		const field = this.firstUnsignedField();
		const flat = this.flattenedPages();
		if (field && flat.length) {
			const pageInfo = flat.find((p) => p.documentID === field.documentID && p.localPage === field.page);
			if (pageInfo) {
				if (pageInfo.globalPage !== this.currentPage()) {
					this.goToPage(pageInfo.globalPage);
				}
				// Usa un timeout leggermente più lungo per garantire il rendering
				setTimeout(() => {
					const el = document.getElementById(`field-${field.signatureFieldID}`);
					if (el) {
						el.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
				}, 250);
			}
		}
	}

	ngOnDestroy() {
		this.layoutService.hideShell.set(false);
	}

	ngOnInit() {
		this.layoutService.hideShell.set(true);
		const nav = this.router.getCurrentNavigation();
		const state = nav?.extras?.state || history.state;
		const navState = state && (state as any).usr ? (state as any).usr : state;

		if (navState?.pratica) {
			this.pratica.set(navState.pratica);
		}

		if (navState?.authenticated) {
			this.isFirstLoadAuthenticated = true;
		}

		if (navState?.operationID) {
			this.operationID.set(navState.operationID);
		}

		if (navState?.idsQueue) {
			this.idsQueue.set(navState.idsQueue);
			this.totalInQueue.set(navState.idsQueue.length + 1);
			this.currentQueueIndex.set(1);
		}

		if (navState?.currentQueueIndex) {
			this.currentQueueIndex.set(navState.currentQueueIndex);
			this.totalInQueue.set(navState.totalInQueue);
		}

		const id = this.idTransaction();
		if (id) {
			this.loadDetail(id);
		}
		this.getGpsInfo();
	}

	getGpsInfo() {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(pos) => {
					this.gpsInfo.set(`${pos.coords.latitude},${pos.coords.longitude}`);
				},
				(err) => {
					console.warn('Geolocation error:', err);
					this.gpsInfo.set('');
				},
			);
		}
	}

	private isFirstLoadAuthenticated = false;

	loadDetail(id: string, isRefresh: boolean = false) {
		if (!isRefresh) this.loadingPage.set(true);
		const alreadyAuth = this.isFirstLoadAuthenticated || !!this.firmaService.getAuthOperationID(id);

		const cachedOpId = this.firmaService.getAuthOperationID(id);
		if (cachedOpId) {
			this.operationID.set(cachedOpId);
		}

		this.isFirstLoadAuthenticated = false;

		this.firmaService.getPraticaDetail(id).subscribe({
			next: (statusRes: PraticaDetailResponse) => {
				const token = statusRes.operationToken;
				const currentOpId = this.operationID();

				if (alreadyAuth && currentOpId) {
					this.praticaDetail.set(statusRes);
					this.setupViewer();
					this.loadingPage.set(false);
					return;
				}

				this.firmaService.startAuthOperation(id, token).subscribe({
					next: (authRes: any) => {
						const opId = authRes.operationID;
						this.operationID.set(opId);

						if (alreadyAuth) {
							this.praticaDetail.set(statusRes);
							this.setupViewer();
							this.loadingPage.set(false);
							return;
						}

						if (statusRes.authFirst) {
							this.loadingPage.set(false);
							const modalRef = this.modalService.open(ModaleOtpComponent, {
								centered: true,
								backdrop: 'static',
								keyboard: false,
								windowClass: 'modal-otp-custom',
							});
							// Prepara gli input per la modalità SMS
							(modalRef.componentInstance as any).mode = 'sms';
							(modalRef.componentInstance as any).phone = statusRes.phone || '';
							(modalRef.componentInstance as any).operationRef = opId;
							(modalRef.componentInstance as any).onResend.subscribe(() => {
								this.firmaService.startAuthOperation(id, token).subscribe({
									next: (newAuthRes: any) => {
										(modalRef.componentInstance as any).operationRef = newAuthRes.operationID;
										(modalRef.componentInstance as any).resetTimer();
									},
									error: () => {
										this.showError('Errore', 'Impossibile ricaricare il codice OTP.');
									},
								});
							});

							modalRef.result.then(
								(otpCode) => {
									if (otpCode) {
										this.firmaService.openTransaction(id, token, parseInt(otpCode, 10), opId).subscribe({
											next: () => {
												this.praticaDetail.set(statusRes);
												this.setupViewer();
											},
											error: () => this.showError('Errore', 'Codice OTP non valido.'),
										});
									} else {
										this.goBack();
									}
								},
								() => this.goBack(),
							);
						} else {
							this.firmaService.openTransaction(id, token, 0, opId).subscribe({
								next: () => {
									this.praticaDetail.set(statusRes);
									this.setupViewer();
									this.loadingPage.set(false);
								},
								error: () => {
									this.loadingPage.set(false);
									this.showError('Errore', 'Impossibile aprire la transazione.');
								},
							});
						}
					},
					error: () => {
						this.loadingPage.set(false);
						this.showError('Errore', "Impossibile avviare l'operazione di firma.");
					},
				});
			},
			error: () => {
				this.loadingPage.set(false);
				this.showError('Errore', 'Impossibile recuperare i dettagli della pratica.');
			},
		});
	}

	setupViewer() {
		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;
		if (!pd || !idTrans || !token || !pd.documents?.length) return;

		const flat: FlattenedPage[] = [];
		let globalCount = 1;

		const requests: any[] = [];
		for (const doc of pd.documents) {
			const totalPages = doc.pages ?? 1;
			for (let i = 1; i <= totalPages; i++) {
				flat.push({
					documentID: doc.documentID,
					localPage: i,
					globalPage: globalCount++,
				});
				requests.push(this.firmaService.getPage(idTrans, doc.documentID, i, token));
			}
		}

		// Ricarica solo se flattenedPages è effettivamente diverso o vuoto
		const currentFlat = this.flattenedPages();
		if (
			currentFlat.length === flat.length &&
			currentFlat.every((p, j) => p.documentID === flat[j].documentID && p.localPage === flat[j].localPage)
		) {
			this.loadingPage.set(false);
			return;
		}

		this.totalPages.set(flat.length);
		this.flattenedPages.set(flat);
		this.pageUrls.set([]);

		forkJoin(requests).subscribe({
			next: (results: any[]) => {
				const urls = results.map((res: any) => {
					const url = `data:image/jpeg;base64,${res.pageBase64}`;
					return this.sanitizer.bypassSecurityTrustUrl(url);
				});
				this.pageUrls.set(urls);
			},
			error: () => {
				this.showError('Errore', 'Errore durante il caricamento delle pagine.');
			},
		});
	}

	getFieldsForPage(globalPageNum: number) {
		const pd = this.praticaDetail();
		const flat = this.flattenedPages();
		if (!pd?.signatureFields || !flat.length) return [];

		const pageInfo = flat[globalPageNum - 1];
		if (!pageInfo) return [];

		return pd.signatureFields.filter((f) => f.page === pageInfo.localPage && f.documentID === pageInfo.documentID);
	}

	getPagesForDocument(docIdx: number) {
		const pd = this.praticaDetail();
		const flat = this.flattenedPages();
		if (!pd?.documents?.[docIdx] || !flat.length) return [];

		const documentID = pd.documents[docIdx].documentID;
		return flat.filter((p) => p.documentID === documentID);
	}

	zoomIn() {
		this.zoomLevel.update((v) => Math.min(v + 0.1, 2));
	}
	zoomOut() {
		this.zoomLevel.update((v) => Math.max(v - 0.1, 0.5));
	}

	toggleFullscreen() {
		console.log('toggleFullscreen - old value:', this.isFullscreen());
		this.isFullscreen.update((v) => !v);
		console.log('toggleFullscreen - new value:', this.isFullscreen());
	}

	prevPage() {
		if (this.currentPage() > 1) {
			this.goToPage(this.currentPage() - 1);
		}
	}

	nextPage() {
		if (this.currentPage() < this.totalPages()) {
			this.goToPage(this.currentPage() + 1);
		}
	}

	onScroll(event: any) {
		const container = event.target;
		const scrollTop = container.scrollTop;
		const children = container.children;
		if (!children.length) return;

		// Calcola la pagina corrente in base a quale elemento è più vicino alla parte superiore del contenitore
		for (let i = 0; i < children.length; i++) {
			const child = children[i] as HTMLElement;
			const childTop = child.offsetTop;
			const childBottom = childTop + child.offsetHeight;

			// Se la metà superiore della pagina (o quasi) è visibile, considerala la pagina corrente
			const threshold = scrollTop + container.clientHeight / 3;
			if (childTop <= threshold && childBottom > threshold) {
				if (this.currentPage() !== i + 1) {
					this.currentPage.set(i + 1);
				}
				break;
			}
		}
	}

	goToPage(num: number) {
		const el = document.getElementById(`page-${num}`);
		if (el) {
			this.currentPage.set(num);
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	signField(field: SignatureField) {
		if (field.firmato || this.modalOpen) return;
		this.modalOpen = true;

		const pd = this.praticaDetail();
		if (pd?.tipoFirma === 'R') {
			const modalRef = this.modalService.open(ModaleOtpComponent, {
				centered: true,
				backdrop: 'static',
				keyboard: false,
				windowClass: 'modal-otp-custom',
			});
			// La modalità è 'remote' per impostazione predefinita
			modalRef.result.then(
				(otp) => {
					this.modalOpen = false;
					if (otp) this.confermaFirmaR(field, otp);
				},
				() => {
					this.modalOpen = false;
				},
			);
		} else {
			const modalRef = this.modalService.open(ModaleConfermaAzioneComponent, { centered: true });
			modalRef.componentInstance.title = 'Conferma firma';
			modalRef.componentInstance.message = 'Sei sicuro di voler firmare questo campo?';
			modalRef.result.then(
				(res) => {
					this.modalOpen = false;
					if (res) this.confermaFirmaO(field);
				},
				() => {
					this.modalOpen = false;
				},
			);
		}
	}

	isLastSignature(field: SignatureField): boolean {
		const pd = this.praticaDetail();
		if (!pd || !pd.signatureFields) return false;
		const unsigned = pd.signatureFields.filter((f) => !f.firmato);
		return unsigned.length === 1 && unsigned[0].signatureFieldID === field.signatureFieldID;
	}

	confermaFirmaO(field: SignatureField) {
		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;
		const opId = this.operationID();
		if (!field || !idTrans || !token || !opId) return;

		this.loadingAction.set(true);
		const body = {
			signatureFieldID: field.signatureFieldID,
			documentID: field.documentID,
			operationID: opId,
			gpsInfo: this.gpsInfo(),
			multi: false,
		};

		const isLast = this.isLastSignature(field);
		this.firmaService.signDocument(idTrans, body, token).subscribe({
			next: (res: any) => {
				this.loadingAction.set(false);
				if (res?.status === 100) {
					this.handleSignatureCompleted();
				} else if (isLast) {
					this.processConferma();
				} else {
					this.loadDetail(idTrans, true);
				}
			},
			error: () => {
				this.loadingAction.set(false);
				this.showError('Errore', 'Errore durante la firma del campo.');
			},
		});
	}

	confermaFirmaR(field: SignatureField, otp: string) {
		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;
		const opId = this.operationID();
		if (!field || !idTrans || !token || !opId) return;

		this.loadingAction.set(true);
		const body = {
			signatureFieldID: field.signatureFieldID,
			documentID: field.documentID,
			operationID: opId,
			gpsInfo: this.gpsInfo(),
			otp: otp,
			multi: this.multiValue(),
		};

		const isLast = this.isLastSignature(field);
		this.firmaService.signDocument(idTrans, body, token).subscribe({
			next: (res: any) => {
				this.loadingAction.set(false);
				if (res?.status === 100) {
					this.handleSignatureCompleted();
				} else if (isLast) {
					this.processConferma();
				} else {
					this.loadDetail(idTrans, true);
					const modalSuc = this.modalService.open(ModaleSuccessoComponent, { centered: true });
					modalSuc.componentInstance.message = 'La firma del documento è avvenuta correttamente.';
				}
			},
			error: () => {
				this.loadingAction.set(false);
				this.showError('Errore', 'Errore durante la firma del campo.');
			},
		});
	}

	conferma() {
		const modalRef = this.modalService.open(ModaleConfermaAzioneComponent, { centered: true });
		modalRef.componentInstance.title = 'Conferma finale';
		modalRef.componentInstance.message = 'Sei sicuro di voler chiudere la pratica e inviare le firme?';
		modalRef.result.then(
			(res) => {
				if (res) this.processConferma();
			},
			() => {},
		);
	}

	processConferma() {
		const idTrans = this.idTransaction();
		const token = this.praticaDetail()?.operationToken;
		if (!idTrans || !token) return;

		this.loadingAction.set(true);
		this.firmaService.closeTransaction(idTrans, token).subscribe({
			next: () => {
				this.loadingAction.set(false);
				this.handleSignatureCompleted();
			},
			error: (err: any) => {
				this.loadingAction.set(false);
				this.showError('Errore', err?.error?.message || 'Errore durante la chiusura della pratica.');
			},
		});
	}

	handleSignatureCompleted() {
		if (this.isPublic()) {
			this.signatureCompleted.set(true);
		} else {
			const modalRef = this.modalService.open(ModaleSuccessoComponent, { centered: true });
			modalRef.componentInstance.message = 'Documento firmato con successo.';
			modalRef.result.then(
				() => {
					const queue = this.idsQueue();
					if (queue.length > 0) {
						const nextId = queue[0];
						const remaining = queue.slice(1);
						this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
							this.router.navigate(['/web/private/firma/dettaglio', nextId], {
								state: {
									idsQueue: remaining,
									totalInQueue: this.totalInQueue(),
									currentQueueIndex: this.currentQueueIndex() + 1,
								},
							});
						});
					} else {
						this.goBack();
					}
				},
				() => this.goBack(),
			);
		}
	}

	openRifiutaModal() {
		const modalRef = this.modalService.open(ModaleRifiutaComponent, { centered: true });
		modalRef.result.then(
			(motivo) => {
				if (motivo !== undefined && motivo !== null) this.rifiuta(motivo);
			},
			() => {},
		);
	}

	rifiuta(motivo: string) {
		const idTrans = this.idTransaction();
		const pd = this.praticaDetail();
		const token = pd?.operationToken;
		if (!idTrans || !token) return;

		this.loadingAction.set(true);
		this.firmaService.refuseTransaction(idTrans, motivo, token).subscribe({
			next: () => {
				this.loadingAction.set(false);
				if (this.isPublic()) {
					this.isRejected.set(true);
					this.signatureCompleted.set(true);
				} else {
					const modalRef = this.modalService.open(ModaleSuccessoComponent, { centered: true });
					modalRef.componentInstance.message = 'Pratica rifiutata con successo.';
					modalRef.result.then(
						() => {
							const queue = this.idsQueue();
							if (queue.length > 0) {
								const nextId = queue[0];
								const remaining = queue.slice(1);
								this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
									this.router.navigate(['/web/private/firma/dettaglio', nextId], {
										state: {
											idsQueue: remaining,
											totalInQueue: this.totalInQueue(),
											currentQueueIndex: this.currentQueueIndex() + 1,
										},
									});
								});
							} else {
								this.goBack();
							}
						},
						() => this.goBack(),
					);
				}
			},
			error: (err: any) => {
				this.loadingAction.set(false);
				this.showError('Errore', err?.error?.message || 'Errore durante il rifiuto del documento.');
			},
		});
	}

	showError(title: string, message: string) {
		const modalRef = this.modalService.open(ModaleErroreGenericoComponent, { centered: true });
		modalRef.componentInstance.title = title;
		modalRef.componentInstance.message = message;
	}

	goBack() {
		if (this.isPublic()) {
			return;
		}
		this.router.navigate(['/web/private/firma']);
	}
}
